import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Lightbulb,
  Compass,
  Pencil,
  MessageSquare,
  Target,
  Printer,
  Download,
  ChevronRight,
} from "lucide-react";
import { BABASAHEB_IMG } from "../components/BabasahebPortrait";
import ChapterSection, { CalloutBox, CHAPTER_STYLES } from "../components/ChapterSection";
import { BOOK_META, CHAPTERS } from "../data/ks2_book";

// Simple decorative imagery per chapter (re-uses palette + symbols)
const COVER_FOR = {
  babasaheb: { tint: "#001B3A", glyph: "BA", label: "Babasaheb" },
  buddha: { tint: "#5B8C5A", glyph: "BU", label: "Buddha" },
  lotus: { tint: "#CFA052", glyph: "✦", label: "Lotus" },
  wheel: { tint: "#1B6EA1", glyph: "☸", label: "Dharma Wheel" },
  phule: { tint: "#8B6E2A", glyph: "PH", label: "Phule" },
  periyar: { tint: "#C44A2D", glyph: "PE", label: "Periyar" },
  ashoka: { tint: "#7B4A2E", glyph: "AS", label: "Ashoka" },
};

function ChapterMarker({ kind, n }) {
  if (kind === "babasaheb") {
    return (
      <div className="ks2-mark ks2-mark-img">
        <img src={BABASAHEB_IMG} alt="Babasaheb" />
      </div>
    );
  }
  const c = COVER_FOR[kind] || COVER_FOR.lotus;
  return (
    <div className="ks2-mark" style={{ background: c.tint }}>
      <span className="ks2-mark-num">Ch. {n}</span>
      <span className="ks2-mark-glyph">{c.glyph}</span>
      <span className="ks2-mark-label">{c.label}</span>
    </div>
  );
}

const ICONS = {
  objective: Target,
  story: BookOpen,
  concept: Lightbulb,
  context: Compass,
  activity: Pencil,
  reflection: MessageSquare,
};

function HtmlList({ items }) {
  return (
    <ul className="bullets">
      {items.map((t, i) => (
        <li key={i} dangerouslySetInnerHTML={{ __html: t }} />
      ))}
    </ul>
  );
}

function HtmlPara({ html }) {
  return <p dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function Ks2BookViewer() {
  const [active, setActive] = useState(1);

  // Auto-print if ?print=1
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("print") === "1") {
      const t = setTimeout(() => window.print(), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Track which chapter is in view
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const id = visible.target.getAttribute("data-chapter-id");
          if (id) setActive(parseInt(id, 10));
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: [0.1, 0.3, 0.6] }
    );
    document.querySelectorAll("[data-chapter-id]").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const units = useMemo(() => {
    const map = new Map();
    CHAPTERS.forEach((c) => {
      if (!map.has(c.unit)) map.set(c.unit, []);
      map.get(c.unit).push(c);
    });
    return Array.from(map.entries());
  }, []);

  const scrollTo = (n) => {
    const el = document.getElementById(`ch-${n}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* Top bar */}
      <div className="bg-navy text-pearl py-4 print:hidden" data-testid="ks2-bar">
        <div className="section-container flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/education"
            className="inline-flex items-center gap-2 text-pearl/80 hover:text-pearl text-sm"
            data-testid="ks2-back"
          >
            <ArrowLeft size={16} /> Back to the series
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href="/api/education/pilot-pack"
              data-testid="ks2-pilot-pack"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-pearl/30 hover:bg-pearl/10 rounded-full text-sm"
            >
              <Download size={16} /> School pilot pack (.zip)
            </a>
            <button
              onClick={() => window.print()}
              data-testid="ks2-print-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-navy hover:bg-gold-light rounded-full text-sm font-medium"
            >
              <Printer size={16} /> Print / save as PDF
            </button>
          </div>
        </div>
      </div>

      <div className="ks2-doc bg-pearl text-navy">
        <div className="ks2-grid">
          {/* SIDEBAR INDEX */}
          <aside className="ks2-aside print:hidden" data-testid="ks2-index">
            <div className="ks2-aside-inner">
              <p className="ks2-aside-eyebrow">Contents</p>
              <h2 className="ks2-aside-title">Student Book</h2>
              <p className="ks2-aside-sub">{BOOK_META.level}</p>
              <nav>
                {units.map(([unit, list]) => (
                  <div key={unit} className="ks2-unit-block">
                    <p className="ks2-unit-label">{unit}</p>
                    <ul>
                      {list.map((c) => (
                        <li key={c.n}>
                          <button
                            onClick={() => scrollTo(c.n)}
                            data-testid={`ks2-toc-${c.n}`}
                            className={`ks2-toc-item ${active === c.n ? "is-active" : ""}`}
                          >
                            <span className="ks2-toc-num">{c.n}.</span>
                            <span className="ks2-toc-title">{c.title}</span>
                            <ChevronRight size={14} className="ks2-toc-chev" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </nav>
            </div>
          </aside>

          {/* MAIN CONTENT */}
          <main className="ks2-main">
            {/* COVER */}
            <article className="ks2-page ks2-cover" data-testid="ks2-cover">
              <div className="ks2-cover-band">
                <span className="ks2-cover-eyebrow">{BOOK_META.series}</span>
              </div>
              <div className="ks2-cover-grid">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-saffron font-semibold">
                    {BOOK_META.level}
                  </p>
                  <h1 className="ks2-cover-title">Student Book</h1>
                  <p className="ks2-cover-sub">{BOOK_META.tagline}</p>
                  <p className="ks2-cover-body">
                    A 12-chapter inquiry-led journey through compassion, dignity, mindfulness and
                    civic responsibility &mdash; written in plain English for British classrooms and
                    family living rooms, for ages 7&ndash;11.
                  </p>
                  <div className="ks2-cover-meta">
                    <div><span>Subject</span>{BOOK_META.subject}</div>
                    <div><span>Edition</span>{BOOK_META.edition}</div>
                    <div><span>Age</span>{BOOK_META.age}</div>
                  </div>
                </div>
                <div className="ks2-cover-portrait">
                  <img src={BABASAHEB_IMG} alt="Babasaheb Dr. B.R. Ambedkar" />
                </div>
              </div>
              <p className="ks2-cover-tag">Education · Equality · Enlightenment</p>
            </article>

            {/* INTRODUCTION */}
            <article className="ks2-page" data-testid="ks2-intro">
              <p className="ks2-eyebrow">A note for teachers and pupils</p>
              <h2 className="ks2-h2">How this book works.</h2>
              <p>
                Every chapter follows the same gentle rhythm. We meet a story &mdash; sometimes from
                ancient India, sometimes from modern Britain. We unpack a single, important idea.
                We try it on for size in our own classroom and our own town. And we end with
                questions to take home.
              </p>
              <p>
                There are <strong>twelve chapters</strong>, grouped into six small units. They are
                designed to be taught roughly in order, but can also stand on their own. A
                Teacher Guide with full notes for the adult sharing the lessons &mdash; differentiation
                ideas and links to wider values-led learning &mdash; is published separately.
              </p>
              <CalloutBox label="A welcome from ABGN-UK">
                We hope your pupils enjoy meeting Bhimrao the brave little reader, the Monkey King
                who became a bridge, the Buddha asking his honest first questions, Savitribai
                walking with two saris, and the kind emperor who turned away from war. These
                stories belong to every child &mdash; including yours.
              </CalloutBox>
            </article>

            {/* CHAPTERS */}
            {CHAPTERS.map((c) => (
              <article
                key={c.n}
                id={`ch-${c.n}`}
                data-chapter-id={c.n}
                className="ks2-page ks2-chapter"
                data-testid={`ks2-chapter-${c.n}`}
              >
                {/* Chapter title block */}
                <header className="ks2-chap-head">
                  <ChapterMarker kind={c.image} n={c.n} />
                  <div>
                    <p className="ks2-eyebrow">{c.unit}</p>
                    <h2 className="ks2-chap-title">
                      Chapter {c.n}. {c.title}
                    </h2>
                    <p className="ks2-chap-meta">{c.duration}</p>
                  </div>
                </header>

                {/* 1. Objective */}
                <ChapterSection
                  icon={ICONS.objective}
                  number="1"
                  title="Learning objectives"
                  tone="objective"
                >
                  <p className="lead">By the end of this lesson, pupils will be able to:</p>
                  <ul>
                    {c.objective.map((o, i) => (
                      <li key={i}>
                        <strong>{o.strong}</strong>
                        {o.rest}
                      </li>
                    ))}
                  </ul>
                  <CalloutBox label="Curriculum link">{c.curriculum}</CalloutBox>
                </ChapterSection>

                {/* 2. Story */}
                <ChapterSection
                  icon={ICONS.story}
                  number="2"
                  title={`Story · ${c.storyTitle}`}
                  tone="story"
                >
                  {c.story.map((p, i) => (
                    <HtmlPara key={i} html={p} />
                  ))}
                  {c.storyDiscussion && (
                    <CalloutBox label="Read aloud · class discussion">
                      {c.storyDiscussion}
                    </CalloutBox>
                  )}
                </ChapterSection>

                {/* 3. Concept */}
                <ChapterSection
                  icon={ICONS.concept}
                  number="3"
                  title={`Concept · ${c.conceptTitle}`}
                  tone="concept"
                >
                  {c.concept.map((p, i) => (
                    <HtmlPara key={i} html={p} />
                  ))}
                  {c.vocab?.length > 0 && (
                    <CalloutBox label="Vocabulary">
                      <dl className="cb-vocab">
                        {c.vocab.map(([term, def]) => (
                          <React.Fragment key={term}>
                            <dt>{term}</dt>
                            <dd>{def}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    </CalloutBox>
                  )}
                </ChapterSection>

                {/* 4. Real-world application */}
                <ChapterSection
                  icon={ICONS.context}
                  number="4"
                  title={`In Britain Today · ${c.contextTitle}`}
                  tone="context"
                >
                  {c.context.map((p, i) => (
                    <HtmlPara key={i} html={p} />
                  ))}
                  {c.contextList?.length > 0 && <HtmlList items={c.contextList} />}
                  {c.contextEnd && <HtmlPara html={c.contextEnd} />}
                </ChapterSection>

                {/* 5. Activity */}
                <ChapterSection
                  icon={ICONS.activity}
                  number="5"
                  title={`Activity · ${c.activityTitle}`}
                  tone="activity"
                >
                  <ol>
                    {c.activity.map((step, i) => (
                      <li key={i} dangerouslySetInnerHTML={{ __html: step }} />
                    ))}
                  </ol>
                </ChapterSection>

                {/* 6. Reflection */}
                <ChapterSection
                  icon={ICONS.reflection}
                  number="6"
                  title="Reflection · Take it home"
                  tone="reflection"
                >
                  <p className="lead">
                    Choose one question to answer in your journal &mdash; or talk about with a grown-up at
                    home.
                  </p>
                  <ol>
                    {c.reflection.map((q, i) => (
                      <li key={i} dangerouslySetInnerHTML={{ __html: q }} />
                    ))}
                  </ol>
                </ChapterSection>
              </article>
            ))}

            {/* BACK MATTER */}
            <article className="ks2-page ks2-back" data-testid="ks2-back">
              <p className="ks2-back-eyebrow">End of Student Book · Phase 1 prototype</p>
              <h2 className="ks2-back-title">Twelve chapters. One quiet revolution.</h2>
              <p className="ks2-back-body">
                The complete <strong>Ambedkarite Buddhist Educational Series &mdash; UK Edition</strong> will
                cover ages 4 through 16, with a Student Book, Workbook and Teacher Guide for each of the
                five age groups. If your school would like to pilot the Junior Programme in 2026,
                we'd love to hear from you.
              </p>
              <Link to="/education#interest" className="ks2-back-cta print:hidden" data-testid="ks2-interest-cta">
                Express interest for your school →
              </Link>
              <p className="ks2-back-meta">
                Ambedkarite Buddhist Global Network UK, United Kingdom · info@ambedkaritebuddhistglobalnetworkuk.com
                <br />
                <span className="italic">Education · Equality · Enlightenment</span>
              </p>
            </article>
          </main>
        </div>
      </div>

      <style>{`
        ${CHAPTER_STYLES}

        .ks2-doc {
          max-width: 1240px;
          margin: 0 auto;
          padding: 32px 16px 64px;
        }
        .ks2-grid {
          display: grid;
          grid-template-columns: 260px 1fr;
          gap: 32px;
          align-items: start;
        }
        @media (max-width: 1024px) {
          .ks2-grid { grid-template-columns: 1fr; }
        }
        .ks2-aside { position: sticky; top: 24px; }
        .ks2-aside-inner {
          background: #fff;
          border: 1px solid rgba(0,27,58,0.12);
          border-radius: 12px;
          padding: 22px 18px;
          max-height: calc(100vh - 48px);
          overflow-y: auto;
        }
        .ks2-aside-eyebrow {
          font-size: 10px; letter-spacing: 0.22em;
          text-transform: uppercase; color: #E07A5F; font-weight: 600;
        }
        .ks2-aside-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 28px; color: #001B3A; margin: 4px 0 0;
        }
        .ks2-aside-sub { font-size: 12px; color: #6b7280; margin: 2px 0 18px; }
        .ks2-unit-block { margin-bottom: 16px; }
        .ks2-unit-label {
          font-size: 10px; letter-spacing: 0.18em;
          text-transform: uppercase; color: #001B3A;
          font-weight: 600; margin-bottom: 6px;
          padding-bottom: 4px;
          border-bottom: 1px dashed rgba(0,27,58,0.15);
        }
        .ks2-aside ul { list-style: none; padding: 0; margin: 0; }
        .ks2-aside li { margin: 0; }
        .ks2-toc-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 8px;
          border-radius: 6px;
          background: transparent;
          color: #1f2937;
          text-align: left;
          font-size: 13px;
          line-height: 1.35;
          border: none;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }
        .ks2-toc-item:hover { background: rgba(207,160,82,0.1); color: #001B3A; }
        .ks2-toc-item.is-active {
          background: #001B3A; color: #FDFBF7;
        }
        .ks2-toc-item.is-active .ks2-toc-num { color: #CFA052; }
        .ks2-toc-num {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 600; color: #CFA052; min-width: 20px;
        }
        .ks2-toc-title { flex: 1; }
        .ks2-toc-chev { opacity: 0.5; flex-shrink: 0; }

        .ks2-main { min-width: 0; }

        .ks2-page {
          background: #fff;
          border: 1px solid rgba(0,27,58,0.12);
          border-radius: 8px;
          padding: 44px 48px;
          margin-bottom: 24px;
          page-break-after: always;
          break-after: page;
        }
        @media (max-width: 640px) {
          .ks2-page { padding: 28px 22px; }
        }

        .ks2-cover {
          background: linear-gradient(180deg, #FDFBF7 0%, #F2E8D2 100%);
        }
        .ks2-cover-band {
          border-bottom: 1px solid rgba(0,27,58,0.15);
          padding-bottom: 12px; margin-bottom: 28px;
        }
        .ks2-cover-eyebrow {
          font-size: 11px; letter-spacing: 0.22em;
          text-transform: uppercase; color: #001B3A; font-weight: 600;
        }
        .ks2-cover-grid {
          display: grid;
          grid-template-columns: 1.4fr 1fr;
          gap: 32px;
          align-items: center;
        }
        @media (max-width: 720px) {
          .ks2-cover-grid { grid-template-columns: 1fr; }
        }
        .ks2-cover-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 56px; line-height: 1; color: #001B3A;
          margin: 12px 0 12px;
        }
        .ks2-cover-sub {
          font-size: 18px; color: #CFA052;
          font-family: 'Cormorant Garamond', serif;
          font-style: italic; margin: 0 0 18px;
        }
        .ks2-cover-body {
          font-size: 15px; color: #1f2937;
          line-height: 1.65; margin-bottom: 22px;
        }
        .ks2-cover-meta {
          display: grid; grid-template-columns: 1fr 1fr 1fr;
          gap: 10px;
          font-size: 12px; color: #4A5568;
        }
        .ks2-cover-meta > div {
          padding: 10px;
          background: rgba(255,255,255,0.6);
          border: 1px solid rgba(0,27,58,0.08);
          border-radius: 6px;
        }
        .ks2-cover-meta span {
          display: block;
          font-size: 9px; letter-spacing: 0.22em;
          text-transform: uppercase; color: #6b7280; margin-bottom: 3px;
        }
        .ks2-cover-portrait {
          aspect-ratio: 4/5;
          overflow: hidden;
          border-radius: 6px;
          border: 1px solid rgba(0,27,58,0.12);
          background: #001B3A;
        }
        .ks2-cover-portrait img {
          width: 100%; height: 100%;
          object-fit: cover; object-position: 50% 30%;
        }
        .ks2-cover-tag {
          margin-top: 32px;
          padding-top: 16px;
          border-top: 1px solid rgba(207,160,82,0.4);
          text-align: center;
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          color: #CFA052;
          font-size: 18px; letter-spacing: 0.05em;
        }

        .ks2-eyebrow {
          font-size: 10px; letter-spacing: 0.22em;
          text-transform: uppercase; color: #E07A5F; font-weight: 600;
        }
        .ks2-h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 36px; color: #001B3A;
          margin: 6px 0 18px; line-height: 1.1;
        }

        .ks2-chap-head {
          display: flex;
          align-items: center;
          gap: 22px;
          margin-bottom: 32px;
          padding-bottom: 20px;
          border-bottom: 2px solid #CFA052;
        }
        .ks2-chap-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 38px; color: #001B3A;
          line-height: 1.05; margin: 4px 0 0;
        }
        @media (max-width: 640px) {
          .ks2-chap-title { font-size: 28px; }
        }
        .ks2-chap-meta { font-size: 12px; color: #6b7280; margin-top: 6px; }

        .ks2-mark {
          flex-shrink: 0;
          width: 96px; height: 120px;
          border-radius: 6px;
          background: #001B3A;
          color: #FDFBF7;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          padding: 8px;
          box-shadow: 0 4px 12px rgba(0,27,58,0.2);
        }
        .ks2-mark-img { padding: 0; overflow: hidden; }
        .ks2-mark-img img { width: 100%; height: 100%; object-fit: cover; object-position: 50% 25%; }
        .ks2-mark-num {
          font-size: 9px; letter-spacing: 0.18em;
          text-transform: uppercase; color: #CFA052; font-weight: 600;
        }
        .ks2-mark-glyph {
          font-family: 'Cormorant Garamond', serif;
          font-size: 36px; line-height: 1; margin: 6px 0;
        }
        .ks2-mark-label {
          font-size: 9px; letter-spacing: 0.12em;
          text-transform: uppercase; opacity: 0.85;
        }

        .bullets { padding-left: 22px; margin: 8px 0 12px; }
        .bullets li { margin-bottom: 6px; }

        .ks2-back {
          background: #001B3A;
          color: #FDFBF7;
          text-align: center;
          border: none;
        }
        .ks2-back-eyebrow {
          font-size: 11px; letter-spacing: 0.22em;
          text-transform: uppercase; color: #CFA052;
        }
        .ks2-back-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 38px; line-height: 1.15;
          margin: 16px auto 18px; max-width: 600px;
        }
        .ks2-back-body {
          color: rgba(253,251,247,0.85);
          max-width: 580px; margin: 0 auto 28px;
          font-size: 15px; line-height: 1.65;
        }
        .ks2-back-cta {
          display: inline-block;
          padding: 12px 28px;
          background: #CFA052;
          color: #001B3A;
          border-radius: 999px;
          text-decoration: none;
          font-weight: 600;
          margin-bottom: 32px;
        }
        .ks2-back-meta {
          padding-top: 20px;
          border-top: 1px solid rgba(253,251,247,0.2);
          font-size: 12px;
          color: rgba(253,251,247,0.7);
        }

        @media print {
          @page { size: A4; margin: 14mm; }
          html, body { background: #fff !important; }
          .ks2-doc { max-width: none; margin: 0; padding: 0; }
          .ks2-grid { display: block; }
          .ks2-aside { display: none; }
          .ks2-page {
            border: none;
            border-radius: 0;
            margin: 0;
            padding: 0 0 12mm 0;
            box-shadow: none;
          }
          .ks2-cover, .ks2-back { padding: 8mm 4mm; }
          .ks2-page:last-child { page-break-after: auto; }
          .ks2-chap-head { margin-bottom: 18px; padding-bottom: 12px; }
        }
      `}</style>
    </>
  );
}
